import { normalizeId, sameId, type OrgWorkspace } from '@vnetwork/margarita-shell/iam';

/** A name the page can say when the lists gave none for the organization. */
const HOME_FALLBACK = 'your organization';
const FOREIGN_FALLBACK = 'the organization you chose';

/**
 * C9: the note on a page that always uses the home organization (Organizations, Teams, Access, Billing, ...) while the
 * person works in a project of another organization. `stored` is the usable choice (`useWorkingProject().project`, null
 * for All projects, a revoked or unchecked one). A foreign workspace is chosen when it is settled and its organization is
 * not the home one (the test `landingView` uses); the home organization must be known. Never on a page inside a
 * Compute or Kubernetes workspace or on a service landing (`onWorkspaceRoute`), never once dismissed. A missing name is
 * replaced by a generic phrase, never printed as empty.
 */
export function homeOrgNote(input: {
  stored: { organizationId: string } | null;
  settled: boolean;
  home: string | undefined;
  homeName: string | undefined;
  foreignName: string | undefined;
  onWorkspaceRoute: boolean;
  dismissed: boolean;
}): string | null {
  const { stored, settled, home, homeName, foreignName, onWorkspaceRoute, dismissed } = input;
  if (!stored || !settled || !normalizeId(home) || sameId(stored.organizationId, home)) return null;
  if (onWorkspaceRoute || dismissed) return null;
  return `This page is about ${normalizeId(homeName) || HOME_FALLBACK}, not ${normalizeId(foreignName) || FOREIGN_FALLBACK}.`;
}

/** sessionStorage key of the organizations whose note the person closed: a JSON array of organization ids. */
export const HOME_NOTE_DISMISSED_KEY = 'margarita.homeOrgNote.dismissed';

function dismissedIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Whether the note for this foreign organization was closed (the next foreign organization gets its own note). */
export function isNoteDismissed(raw: string | null | undefined, organizationId: string): boolean {
  return dismissedIds(raw).some((id) => sameId(id, organizationId));
}

/** The stored text after closing the note for `organizationId`. */
export function withNoteDismissed(raw: string | null | undefined, organizationId: string): string {
  const ids = dismissedIds(raw);
  return JSON.stringify(ids.some((id) => sameId(id, organizationId)) ? ids : [...ids, normalizeId(organizationId)]);
}

/**
 * The name of a workspace page: the service's own navigation label when it has one (`quotas` is `Quotas & Usage`),
 * otherwise the slug read as words (`some-page` is `Some page`); '' is the dashboard.
 */
export function workspacePageLabel(page: string, serviceLabel?: string): string {
  if (serviceLabel) return serviceLabel;
  const words = page.split('-').filter(Boolean);
  if (words.length === 0) return 'Dashboard';
  return [words[0].charAt(0).toUpperCase() + words[0].slice(1), ...words.slice(1)].join(' ');
}

/**
 * D2: the document title while another organization's workspace is open, `<page> · <Organization name>`; null for the
 * home workspace and for any page outside a workspace (they keep the title they have).
 */
export function workspaceDocumentTitle(input: {
  home: string | undefined;
  workspaceOrganizationId: string | undefined;
  page: string;
  /** The label the service gives `page` (`WorkspaceService.pageLabel`). */
  pageLabel?: string;
  organizationName: string | undefined;
}): string | null {
  const { home, workspaceOrganizationId, page, pageLabel, organizationName } = input;
  if (!isForeignWorkspace(home, workspaceOrganizationId)) return null;
  return `${workspacePageLabel(page, pageLabel)} · ${organizationLabel(workspaceOrganizationId, organizationName)}`;
}

/**
 * P1: below 768px the switcher is hidden, so the page says whose workspace it is: the Shared badge and the organization
 * name, only while the URL is a workspace of another organization. null otherwise.
 */
export function mobileOrgBadge(input: {
  home: string | undefined;
  workspaceOrganizationId: string | undefined;
  organizationName: string | undefined;
}): { organizationName: string } | null {
  const { home, workspaceOrganizationId, organizationName } = input;
  if (!isForeignWorkspace(home, workspaceOrganizationId)) return null;
  return { organizationName: organizationLabel(workspaceOrganizationId, organizationName) };
}

/** A workspace of another organization: both ids are known and differ. */
function isForeignWorkspace(home: string | undefined, workspaceOrganizationId: string | undefined): boolean {
  return !!normalizeId(home) && !!normalizeId(workspaceOrganizationId) && !sameId(home, workspaceOrganizationId);
}

function organizationLabel(organizationId: string | undefined, name: string | undefined): string {
  return normalizeId(name) || `Organization ${normalizeId(organizationId)}`;
}

/**
 * The home organization's name: the one the shared-projects list gives for it (that list may hold no home entry), else
 * the name of the home workspace of any service.
 */
export function homeOrganizationName(input: {
  fromSharedProjects: string | undefined;
  workspaces: readonly Pick<OrgWorkspace, 'home' | 'organizationId' | 'organizationName'>[];
  home: string | undefined;
}): string | undefined {
  const { fromSharedProjects, workspaces, home } = input;
  return (
    normalizeId(fromSharedProjects) ||
    workspaces.find((w) => w.home && sameId(w.organizationId, home) && normalizeId(w.organizationName))?.organizationName.trim() ||
    undefined
  );
}

/** A page that is inside a service's workspace or is the landing of one: the home-organization note never shows there. */
export function isServicePage(input: { pathname: string; insideWorkspace: boolean; landings: readonly string[] }): boolean {
  const { pathname, insideWorkspace, landings } = input;
  return insideWorkspace || landings.some((l) => pathname === l || pathname.startsWith(`${l}/`));
}

type NoteStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** The stored dismissals; blocked or missing storage reads as none (the note comes back on the next page load). */
export function readNoteDismissals(storage: NoteStorage | undefined): string | null {
  try {
    return storage?.getItem(HOME_NOTE_DISMISSED_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Closes the note for `organizationId`: returns the new stored text, written when the storage lets it. */
export function dismissNote(storage: NoteStorage | undefined, raw: string | null, organizationId: string): string {
  const next = withNoteDismissed(raw, organizationId);
  try {
    storage?.setItem(HOME_NOTE_DISMISSED_KEY, next);
  } catch {
    /* storage blocked: closed until the page reloads */
  }
  return next;
}
